import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import {
  fetchAdminBlogPosts,
  createBlogPost,
  updateBlogPost,
  deleteBlogPost,
} from "@/lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  LogOut,
  ExternalLink,
  Eye,
  EyeOff,
} from "lucide-react";

const emptyForm = {
  id: null,
  title: "",
  excerpt: "",
  content: "",
  cover_image: "",
  author: "Spherio Living",
  tagsText: "",
  published: true,
};

export default function AdminBlog() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setPosts(await fetchAdminBlogPosts());
    } catch {
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setForm({ ...emptyForm, author: user?.name || "Spherio Living" });
    setDialogOpen(true);
  };

  const openEdit = (post) => {
    setForm({
      id: post.id,
      title: post.title,
      excerpt: post.excerpt || "",
      content: post.content || "",
      cover_image: post.cover_image || "",
      author: post.author || "Spherio Living",
      tagsText: (post.tags || []).join(", "),
      published: !!post.published,
    });
    setDialogOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    const payload = {
      title: form.title.trim(),
      excerpt: form.excerpt.trim(),
      content: form.content,
      cover_image: form.cover_image.trim() || null,
      author: form.author.trim() || "Spherio Living",
      tags: form.tagsText
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      published: form.published,
    };
    try {
      if (form.id) {
        const updated = await updateBlogPost(form.id, payload);
        setPosts((ps) => ps.map((p) => (p.id === form.id ? updated : p)));
        toast.success("Post updated");
      } else {
        const created = await createBlogPost(payload);
        setPosts((ps) => [created, ...ps]);
        toast.success("Post published");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await deleteBlogPost(id);
      setPosts((ps) => ps.filter((p) => p.id !== id));
      toast.success("Post deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const togglePublished = async (post) => {
    try {
      const updated = await updateBlogPost(post.id, {
        published: !post.published,
      });
      setPosts((ps) => ps.map((p) => (p.id === post.id ? updated : p)));
      toast.success(updated.published ? "Published" : "Unpublished");
    } catch {
      toast.error("Update failed");
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate("/", { replace: true });
  };

  const shown = useMemo(() => {
    const q = search.toLowerCase();
    return posts.filter(
      (p) =>
        !q ||
        p.title.toLowerCase().includes(q) ||
        (p.excerpt || "").toLowerCase().includes(q) ||
        (p.author || "").toLowerCase().includes(q)
    );
  }, [posts, search]);

  return (
    <div className="min-h-screen bg-[#F5F2ED]">
      <header className="bg-white border-b border-black/5 sticky top-0 z-30">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="font-head text-xl font-extrabold tracking-tight">
            SPHERIO LIVING<span className="text-[#1C1A17]">.</span>{" "}
            <span className="text-sm font-sans font-medium text-[#5C574F]">
              Admin · Blog
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <Link
              to="/admin"
              data-testid="admin-blog-leads-link"
              className="text-sm text-[#5C574F] hover:text-[#1C1A17]"
            >
              Leads
            </Link>
            {user?.picture && (
              <img
                src={user.picture}
                alt={user.name}
                className="h-8 w-8 rounded-full object-cover"
              />
            )}
            <div className="hidden sm:block text-sm">
              <div className="font-medium leading-tight">{user?.name}</div>
              <div className="text-[#5C574F] text-xs">{user?.email}</div>
            </div>
            <button
              data-testid="admin-blog-refresh"
              onClick={load}
              className="inline-flex items-center gap-2 text-sm text-[#5C574F] hover:text-[#1C1A17]"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />{" "}
              Refresh
            </button>
            <button
              data-testid="admin-blog-logout"
              onClick={handleLogout}
              className="inline-flex items-center gap-2 text-sm text-[#5C574F] hover:text-[#1C1A17]"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-6 lg:px-10 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <h1 className="font-head text-3xl sm:text-4xl font-medium tracking-tight">
            Blog posts
          </h1>
          <Button
            data-testid="admin-blog-new"
            onClick={openCreate}
            className="bg-[#1C1A17] hover:bg-[#1C1A17]/90 text-white rounded-full h-11 px-5"
          >
            <Plus className="h-4 w-4 mr-1.5" /> New post
          </Button>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-4 sm:items-center">
          <Input
            data-testid="admin-blog-search"
            placeholder="Search title, excerpt, author…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-11 rounded-xl bg-white max-w-sm"
          />
          <div className="text-sm text-[#5C574F]">
            {posts.length} total · {posts.filter((p) => p.published).length} published
          </div>
        </div>

        <div className="mt-6 overflow-x-auto rounded-2xl border border-black/10 bg-white">
          <table
            className="w-full text-sm"
            data-testid="admin-blog-table"
          >
            <thead>
              <tr className="text-left text-[#5C574F] border-b border-black/10">
                <th className="p-4 font-medium">Title</th>
                <th className="p-4 font-medium">Author</th>
                <th className="p-4 font-medium">Tags</th>
                <th className="p-4 font-medium">Created</th>
                <th className="p-4 font-medium">Status</th>
                <th className="p-4 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="p-10 text-center text-[#5C574F]"
                    data-testid="admin-blog-empty"
                  >
                    No posts yet. Click{" "}
                    <span className="font-semibold text-[#1C1A17]">
                      New post
                    </span>{" "}
                    to write your first article.
                  </td>
                </tr>
              )}
              {shown.map((p) => (
                <tr
                  key={p.id}
                  className="border-b border-black/5 align-top hover:bg-[#F5F2ED]"
                  data-testid={`admin-blog-row-${p.id}`}
                >
                  <td className="p-4 max-w-md">
                    <div className="font-medium text-[#0A0A0A]">{p.title}</div>
                    {p.excerpt && (
                      <div className="text-xs text-[#5C574F] mt-1 line-clamp-2">
                        {p.excerpt}
                      </div>
                    )}
                    <Link
                      to={`/blog/${p.slug}`}
                      target="_blank"
                      className="mt-1 inline-flex items-center gap-1 text-xs text-[#5C574F] hover:text-[#1C1A17]"
                    >
                      /blog/{p.slug} <ExternalLink className="h-3 w-3" />
                    </Link>
                  </td>
                  <td className="p-4 text-[#5C574F]">{p.author}</td>
                  <td className="p-4 text-[#5C574F]">
                    <div className="flex flex-wrap gap-1">
                      {(p.tags || []).map((t) => (
                        <span
                          key={t}
                          className="px-2 py-0.5 rounded-full bg-[#F5F1EA] text-[#1C1A17] text-[11px]"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="p-4 text-[#5C574F] text-xs whitespace-nowrap">
                    {new Date(p.created_at).toLocaleDateString("en-IN", {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </td>
                  <td className="p-4">
                    <button
                      onClick={() => togglePublished(p)}
                      data-testid={`admin-blog-toggle-${p.id}`}
                      className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${
                        p.published
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {p.published ? (
                        <Eye className="h-3 w-3" />
                      ) : (
                        <EyeOff className="h-3 w-3" />
                      )}
                      {p.published ? "Published" : "Draft"}
                    </button>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => openEdit(p)}
                        data-testid={`admin-blog-edit-${p.id}`}
                        className="p-2 text-[#5C574F] hover:text-[#1C1A17] rounded-lg hover:bg-[#F5F1EA]"
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(p.id)}
                        data-testid={`admin-blog-delete-${p.id}`}
                        className="p-2 text-[#5C574F] hover:text-red-600 rounded-lg hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent
          className="max-w-2xl max-h-[90vh] overflow-y-auto"
          data-testid="admin-blog-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-head text-2xl">
              {form.id ? "Edit post" : "New post"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label htmlFor="blog-title">Title</Label>
              <Input
                id="blog-title"
                data-testid="admin-blog-form-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Kitchen trends for 2026"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="blog-excerpt">Excerpt</Label>
              <Textarea
                id="blog-excerpt"
                data-testid="admin-blog-form-excerpt"
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                placeholder="A short summary that appears on cards and above the article."
                rows={2}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="blog-cover">Cover image URL</Label>
              <Input
                id="blog-cover"
                data-testid="admin-blog-form-cover"
                value={form.cover_image}
                onChange={(e) =>
                  setForm({ ...form, cover_image: e.target.value })
                }
                placeholder="https://…"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="blog-author">Author</Label>
                <Input
                  id="blog-author"
                  data-testid="admin-blog-form-author"
                  value={form.author}
                  onChange={(e) =>
                    setForm({ ...form, author: e.target.value })
                  }
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="blog-tags">Tags (comma-separated)</Label>
                <Input
                  id="blog-tags"
                  data-testid="admin-blog-form-tags"
                  value={form.tagsText}
                  onChange={(e) =>
                    setForm({ ...form, tagsText: e.target.value })
                  }
                  placeholder="Kitchens, Trends"
                />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="blog-content">
                Content (supports <code>##</code> heading, <code>###</code>{" "}
                subheading, and <code>-</code> bullets)
              </Label>
              <Textarea
                id="blog-content"
                data-testid="admin-blog-form-content"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={12}
                className="font-mono text-sm"
                placeholder={"## Section title\n\nParagraph text goes here.\n\n- bullet one\n- bullet two"}
              />
            </div>
            <div className="flex items-center justify-between rounded-xl border border-black/10 p-4">
              <div>
                <div className="text-sm font-medium">Published</div>
                <div className="text-xs text-[#5C574F]">
                  Visible on the public /blog page
                </div>
              </div>
              <Switch
                checked={form.published}
                onCheckedChange={(v) => setForm({ ...form, published: v })}
                data-testid="admin-blog-form-published"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              data-testid="admin-blog-form-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="admin-blog-form-save"
              className="bg-[#1C1A17] hover:bg-[#1C1A17]/90 text-white"
            >
              {saving ? "Saving…" : form.id ? "Save changes" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Toaster position="top-center" richColors />
    </div>
  );
}
